/**
 * MeetingRow — reusable row component for meeting lists on dashboard / drafts
 * / project pages. Includes inline delete button (hover-reveal) that
 * soft-deletes via POST /meetings/:id/soft-delete.
 */
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/apiClient.ts';

export interface MeetingListItem {
  id: string;
  title: string;
  meetingDate: string;
  notesDate: string;
  projectId: string;
  /** Project display name, joined on the server. Null for legacy /
   *  unexpected rows where the project join missed. */
  projectName?: string | null;
  status: 'draft' | 'finalized' | 'soft_deleted';
  ownerUserId: string;
  templateVariant: string;
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string;
  version: number;
  /** Number of attendees captured at creation from the calendar event.
   *  0 when the meeting wasn't prefilled from Outlook. */
  attendeeCount?: number;
  /** Server-derived short summary (from emailSummaryText or topic titles) */
  summary?: string | null;
}

export function MeetingRow({ meeting }: { meeting: MeetingListItem }) {
  const qc = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => api.post(`/meetings/${meeting.id}/soft-delete`),
    onSuccess: () => {
      // Invalidate every meetings query so all lists refresh
      qc.invalidateQueries({ queryKey: ['meetings'] });
      qc.invalidateQueries({ queryKey: ['projects-with-counts'] });
    },
  });

  function handleDelete(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Delete "${meeting.title}"? You can restore from admin.`)) return;
    deleteMutation.mutate();
  }

  const errMsg =
    deleteMutation.error instanceof ApiError
      ? deleteMutation.error.message
      : deleteMutation.error instanceof Error
        ? deleteMutation.error.message
        : null;

  // Mockup .drafts-list__row: padding 16/22, hover bg pine-soft.
  // Title 15px Aeonik medium charcoal, meta 12px charcoal/60 with
  // bullet separators, summary 13px charcoal/75 line-clamp-2.
  // Delete: 28px circle, charcoal/55 → terra-cotta on hover,
  // opacity-0 → 1 on row hover.
  return (
    <li className="group relative">
      <Link
        to={`/meetings/${meeting.id}/edit`}
        className="grid grid-cols-[1fr_auto] gap-4 px-[22px] py-4 no-underline transition-colors hover:bg-pine/[0.06]"
      >
        <div className="min-w-0">
          <div className="truncate text-[15px] font-medium text-charcoal">
            {meeting.title}
          </div>
          {/* Meta line: dot separator (3px circles) instead of the
              old "·" character so the row reads as a labeled list,
              not prose. */}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-charcoal/60">
            <span>Meeting {meeting.meetingDate}</span>
            <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-current opacity-50" />
            <span className={meeting.status === 'finalized' ? 'text-pine' : 'text-charcoal/60'}>
              {meeting.status}
            </span>
            <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-current opacity-50" />
            <span>Updated {new Date(meeting.updatedAt).toLocaleDateString()}</span>
          </div>
          {meeting.summary && (
            <div className="mt-1.5 line-clamp-2 text-[13px] font-light leading-[1.5] text-charcoal/75">
              {meeting.summary}
            </div>
          )}
        </div>
        {/* 28px circle delete button — opacity-0 by default, fades in
            on row hover. Hover terra-cotta tint (was red-600).
            self-start so it stays at the top of tall rows. */}
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="flex h-7 w-7 shrink-0 self-start items-center justify-center rounded-full text-[14px] text-charcoal/55 opacity-0 transition-all hover:bg-terra-cotta/[0.15] hover:text-terra-cotta group-hover:opacity-100 disabled:opacity-50"
          aria-label={`Delete ${meeting.title}`}
          title="Delete draft"
        >
          {deleteMutation.isPending ? '…' : '✕'}
        </button>
      </Link>
      {errMsg && (
        <div className="border-t border-terra-cotta/20 bg-terra-cotta/[0.08] px-[22px] py-1.5 text-[11px] text-terra-cotta">
          Delete failed: {errMsg}
        </div>
      )}
    </li>
  );
}
